//tablesection

import { useEffect, useRef, useState } from 'react';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { exportGetFinopsResourceMappingList, getFinopsResource, getFinopsResourceMappingList } from 'service/service';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import RowTypography from 'components/Table/RowTypography';
import LoaderComponent from 'components/Loader';
import { Box, Button, Checkbox, Drawer, Stack } from '@mui/material';
import SearchFilterDropdown from 'components/Inputs/ResourceMapping/SearchFilterDropdown';
import DialogueModal from 'components/Modal/DialogModal';
import BulkEditModalConfirm from './BulkEditModalConfirm';
import ResourceAllocationModal from './ResourceAllocationModal';
import HistoryIcon from '@mui/icons-material/History';
import UpdateHistory from './UpdateHistory';
import ResolveAllocationConflictsModal from './ResolveAllocationConflictsModal';
import AllocationConflictModal from './AllocationConflictModal';
import { ReactComponent as AllocationIcon } from '../../../assets/allocation_big_button.svg';
import { childInfoTransformData } from 'utils/common-util';
import eventEmitter from 'SnackBarEventEmitter';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import colors from 'assets/theme/base/colors';
import { columnNames, formatAllocPercent } from './ResourceMappingHelper';

interface Row {
	id: string;
	[key: string]: any;
	Resource: string;
	Resourcetype: string;
	ResourceGroup: string;
	DFProjectcode: string;
	DFSectorname: string;
	pepwiseEnvironment: string;
	ProjectName: string;
	BillingInformation: string;
	Subscription: string;
	MeterName: string;
	selected: boolean;
	DAI: string;
	ClusterName: string;
	SqlEndpointId: string;
	ConsumedService: string;
	BusinessSector: string;
	OrgGrpCodeProjectCode: string;
	projectno: string;
	ClusterJobSource: string;
	DatabricksInstancePoolId: string;
	DFDataassetname: string;
	kubernetespvcnamespace: string;
	DNAI_Value: string;
	allocationId: string;
	AllocationCount: string;
	Allocpercent: string;
	pepwisesector: string;
	pepwiseproject: string;
	pepwiseprogram: string;
	actions: string;
}

interface Option {
	value: string;
	label: string;
}

type ModalWidth = 'sm' | 'md' | 'lg' | 'xl';

const defaultColumnNames = [
	'Actions',
	'HeaderCheckboxMapping',
	'Resource',
	'Resource Type',
	'Resource Group',
	'Allocation %',
	'Pepwise Sector',
	'Pepwise Environment',
	'Pepwise Project',
	'Pepwise Program',
	'Business Sector',
	'Sector',
	'Environment',
	'Project Name',
	'Project No',
	'Program',
	'Consumed Service',
	'Location',
	'Org Grp Code Project Code',
	'Billing Information',
	'Subscription',
	'Cluster Name',
	'Cluster Job Source',
	'Databricks Instance Pool Id',
	'DF Sectorname',
	'DF Projectcode',
	'DF Data Assetname',
	'Sql Endpoint Id',
	'Kubernetes pvc Namespace',
	// 'Meter Category',
	'Meter Name',
	'D & AI',
];

const parseFilter = (filterInfo: any, name?: string) => {
	// Call only filterable columns and reset the filtering in case of wrong/empty column
	const parsedFilter: any = {};
	Object.keys(filterInfo).forEach((key: any) => {
		if (key !== name && filterInfo[key] && filterInfo[key].length) {
			parsedFilter[key] = filterInfo[key];
		}
	});
	return parsedFilter;
};

const getInputOptions = async (input: string, field: string) => {
	const fieldsRes = await getFinopsResourceMappingList({ searchField: field, searchText: input });
	const options: Option[] = fieldsRes.data.result.filter(Boolean).map((item: string) => ({ value: item, label: item }));
	return options;
};

const initialEditRowState = {
	rowsToUpdate: {}
};

const tableTitles: Record<string, string> = {
	ResourceGroup_tree: 'Resource Groups',
	SqlEndpointId_tree: 'DBSQL Clusters',
	ClusterName_inttree: 'Interactive Clusters',
	ClusterJobSource_jobtree: 'Job Clusters',
};

type CheckboxFormatterProps = {
  row: any;
  bulkSelectedRows: Record<string, Row>;
  setBulkSelectedRows: React.Dispatch<React.SetStateAction<Record<string, Row>>>;
  setTableDataList: React.Dispatch<React.SetStateAction<any[]>>;
  handleTreeViewCheck: (row: any, updatedRows: any) => void;
};

const CheckboxFormatter: React.FC<CheckboxFormatterProps> = ({
  row,
  bulkSelectedRows,
  setBulkSelectedRows,
  setTableDataList,
  handleTreeViewCheck
}) => {
	const isParentRow = Boolean(row.values && Array.isArray(row.values));
	return isParentRow ? (
		<Checkbox
		sx={{
			padding: 0,
			'& .MuiSvgIcon-root': {
			color: `${colors.table.checkBox} !important`,
			}
		}}
		checked={!!bulkSelectedRows[row.id]}
		onChange={(e: any) => {
			const checked = e.target.checked;
			const updatedRow = { ...row };

			setBulkSelectedRows((prevSelectedRows) => {
			const updatedSelectedRows = { ...prevSelectedRows };

			if (checked) {
				// **Flat View**: Add the row directly
				updatedSelectedRows[row.id] = row;

				// **Tree View**: If row has children (values), add each child row
				handleTreeViewCheck(row, updatedSelectedRows);
			} else {
				// Deselect the row (Flat or Tree)
				let removeRowIds = [row.id];

				// **Tree View**: If row has children, add their IDs for removal
				if (row.values && Array.isArray(row.values)) {
				removeRowIds = removeRowIds.concat(
					row.values
					.map((childRow: any) => {
						let nestedRemove = [];
						if (childRow.values && Array.isArray(childRow.values)) {
						nestedRemove = childRow.values.map((nestedChildRow: any) => nestedChildRow.id);
						}

						return [childRow.id, ...nestedRemove];
					})
					.flat()
				);
				}

				// **Flat View**: Remove the row itself (if not a tree view)
				for (let key in updatedSelectedRows) {
				if (removeRowIds.includes(key)) {
					delete updatedSelectedRows[key];
				}
				}
			}

			return updatedSelectedRows;
			});

			setTableDataList((prevRows) =>
			prevRows.map((prevRow) => (prevRow.id === updatedRow.id ? updatedRow : prevRow))
			);
		}}
		/>
	) : null;
};

type AllocationFormatterProps = {
  row: any;
  handleResourceColumnClick: (row: any) => void;
};

const ActionFormatter: React.FC<AllocationFormatterProps> = ({
  row,
  handleResourceColumnClick
}) => {
  	const isRootParent = !!(Array.isArray(row.values) && !row.values[0]?.values);
	return isRootParent ? (
		<Stack direction="row" alignItems="center" height="100%">
			<Box lineHeight={0} sx={{ display: 'flex', alignItems: 'center' }}>
				<AllocationIcon
					title="Allocation"
					style={{ width: '24px', height: '24px', cursor: 'pointer' }}
					onClick={() => handleResourceColumnClick(row)}
				/>
			</Box>
		</Stack>
	) : null;
};

type ResourceCellFormatterProps = {
  row: any;
  column: any;
  isCellSelected: boolean;
  idx: number;
  groupBy: string;
  editedRows: any;
  onRowChange: any;
};

const TableSection = ({ queryPreference, queryProps, totalPages, setTotalPages, sortingInfo, setSortingInfo, isQueryPrefChange, setIsQueryPrefChange, isReapplied, abortControllerRef, setIsAbort, onResourceTypeCostChange }: any) => {
	const [tableTitle, setTableTitle] = useState('Resources');
	const isTreeView = queryPreference.split('_')[1] === 'tree';

	const headerCheckboxRef = useRef<HTMLInputElement>(null);
	const [headerCheckbox, setHeaderCheckbox] = useState(false);

	const collapseChildren = (childrenToCollapse: Row[], newRows: Row[]) => {
		for (const child of childrenToCollapse) {
			const childIndex = newRows.findIndex((r) => r.id === child.id);
			if (child.isExpanded && child.values) {
				collapseChildren(child.values, newRows);
				newRows.splice(childIndex + 1, child.values.length);
			}
		}
	};

	function toggleSubRowRG(id: string, resourceTypes: any) {
		setTableDataList((prev) => {
			const rows = [...prev];
			const rowIndex = rows.findIndex((r: any) => r.id === id);
			const row = rows[rowIndex];
			const newRows = [...rows];
			let children: Row[] = [];
			let childRows: Row[] = [];
			if (!row.isExpanded) {
				if (resourceTypes?.length > 0) {
					childRows = resourceTypes;
				}
				children = childRows.map((x: any) => ({
					...x,
					isChildren: true
				}));
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: true, children };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = true;
			} else {
				children = row.children ?? [];
				collapseChildren(children, newRows);
				newRows.splice(Number(rowIndex) + 1, children.length);
				newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = false;
			}
			return newRows;
		});
	}

	function applyStoredChildren(
		storageRows: any[],
		row: any,
		rowIndex: number,
		newRows: any[],
		setTableDataList: Function
	): boolean {
		const stored = storageRows.find((strRow) => strRow.id === row.id);
		if (!stored) return false;

		newRows.splice(rowIndex + 1, 0, ...stored.children);
		newRows[rowIndex] = {
			...row,
			isExpanded: true,
			children: stored.children
		};

		const updated = [...newRows];
		setTableDataList(updated);
		return true;
	}

	function persistChildren(row: any, children: any[]) {
		const existing = localStorage.getItem('finopsGetResource');
		const newEntry = [{
			id: row.id,
			Id: row.Id,
			groupId: row.groupId,
			children
		}];

		if (existing) {
			localStorage.setItem(
			'finopsGetResource',
			JSON.stringify(JSON.parse(existing).concat(newEntry))
			);
		} else {
			localStorage.setItem('finopsGetResource', JSON.stringify(newEntry));
		}
	}

	function handleResourceSuccess(
		res: any,
		row: any,
		rowIndex: any,
		newRows: any[]
	) {
		let children: Row[] = [];
		let childRows: Row[] = [];
		Object.entries(res.data).forEach(([key, value]: any) => {
			childRows = childInfoTransformData(value);
		});
		children = childRows.map((x: any) => {
			return { ...x, isChildren: true };
		});
		newRows.splice(Number(rowIndex) + 1, 0, ...children);
		newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children };
		const newUpdatedRows = [...newRows];

		constRows = newUpdatedRows
		setTableDataList(newUpdatedRows);
		const stBoxRows: any = localStorage.getItem('finopsGetResource');
		const tempRows = [{
			id: row.id,
			Id: row.Id,
			groupId: row.groupId,
			children: children
		}]
		if (stBoxRows) {
			let tempStorageBoxRows = JSON.parse(stBoxRows)
			tempStorageBoxRows = tempStorageBoxRows.concat(tempRows);
			localStorage.setItem('finopsGetResource', JSON.stringify(tempStorageBoxRows))
		} else {
			localStorage.setItem('finopsGetResource', JSON.stringify(tempRows))
		}
	}

	let constRows: any = [];
	async function toggleSubRow(id: string, resourceTypes: any) {
		const rows = constRows.length ? constRows : tableDataList;

		const [groupBy, viewType] = queryPreference.split('_');
		const rowIndex = rows.findIndex((r: any) => r.id === id);
		const row = rows[rowIndex];
		const newRows = [...rows];
		let children: Row[] = [];
		let childRows: Row[] = [];
		if (!row.isExpanded) {
			const boxRows: any = localStorage.getItem('finopsGetResource');
			const storageRows = boxRows ? JSON.parse(boxRows) : [];
			let hasStrRow = false;
			storageRows.filter((strRow: any) => {
				if (strRow.id == row.id) {
					hasStrRow = true;
					newRows.splice(Number(rowIndex) + 1, 0, ...strRow.children);
					newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children: strRow.children };
					const newUpdatedRows = [...newRows];
					constRows = newUpdatedRows
					setTableDataList(newUpdatedRows);
				}
			});
			
			if (!hasStrRow) {
				let payload = tableDataList.filter((data) => data.id === id)
				const groupId = payload[0].groupId;
				const res = await getFinopsResource({
					groupId, 
					...((viewType === 'tree' || viewType === 'inttree' || viewType === 'jobtree') && {
						groupBy
					}),
					viewType
				});
				if (res.success) {
					handleResourceSuccess(res, row, rowIndex, newRows);
				}
			}

			if (resourceTypes?.length > 0) {
				childRows = resourceTypes;
			}
			children = childRows.map((x: any) => {
				return { ...x, isChildren: true };
			});
			newRows.splice(Number(rowIndex) + 1, 0, ...children);
			newRows[rowIndex] = { ...row, isExpanded: true, children };
			row.isExpanded = true;
		}
		else {
			children = row.children ?? [];
			collapseChildren(children, newRows);
			newRows.splice(Number(rowIndex) + 1, children.length);
			newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
			const newUpdatedRows = [...newRows];
			constRows = newUpdatedRows
			setTableDataList(newUpdatedRows);
			localStorage.setItem('boxRows', JSON.stringify(newRows));
			row.isExpanded = false;
		}
	}

	const handleTreeViewCheck = (treeRow: any, updatedSelectedRows: any) => {
		if (treeRow.values && Array.isArray(treeRow.values)) {
			treeRow.values.forEach((childRow: any) => {
				updatedSelectedRows[childRow.id] = childRow;
				if (childRow.values && Array.isArray(childRow.values)) {
					childRow.values.forEach((nestedChildRow: any) => {
						updatedSelectedRows[nestedChildRow.id] = nestedChildRow;
					});
				}
			});
		}
	};

	const checkboxFormatter = () => {
		const formatter = ({ row }: any) => (
			<CheckboxFormatter
				row={row}
				bulkSelectedRows={bulkSelectedRows}
				setBulkSelectedRows={setBulkSelectedRows}
				setTableDataList={setTableDataList}
				handleTreeViewCheck={handleTreeViewCheck}
			/>
		);
		return formatter;
	};

	const actionFormatter = () => {
		const formatter = ({ row }: any) => (
			<ActionFormatter
			row={row}
			handleResourceColumnClick={handleResourceColumnClick}
			/>
		);

		return formatter;
	};

	const isEmptyCellValue = (value: any) => {
		return value === null || value === undefined || value === '';
	};

	const getCellDisplayValue = (value: any) => {
		if (isEmptyCellValue(value)) {
			return 'Unknown';
		}

		return value;
	};

	const getUnknownCellColor = (value: any) => {
		if (isEmptyCellValue(value)) {
			return '#71758F !important';
		}

		return undefined;
	};

	const renderDefaultResourceCell = ({
		row,
		column,
		idx,
		hasChildren,
		isCellSelected,
		editedRows,
		groupBy,
	}: any) => {
		const isGroupBy = column.key.toLowerCase() === groupBy.toLowerCase();
		const isCellUpdated = row.groupId && editedRows[row.groupId]?.[column.key];
		const unknownCellColor = getUnknownCellColor(row[column.key]);
		return (
			<Box display="flex">
				{idx === 0 && isGroupBy && hasChildren && row.isResource === false 
				&& ((groupBy !== 'ResourceGroup' && row.group_count > 1) || (groupBy === 'ResourceGroup' && row.values.length > 0)) && (
					<CellExpanderFormatter
						isCellSelected={isCellSelected}
						expanded={row.isExpanded === true}
						onCellExpand={() => {
							groupBy === 'ResourceGroup' ? toggleSubRowRG(row.id, row.values) : toggleSubRow(row.id, row.values);
						}}
					/>
				)}
				{idx === 1 && column.key === 'Resource' && row.isResource && hasChildren && row.group_count > 1  && (
					<CellExpanderFormatter
						isCellSelected={isCellSelected}
						expanded={row.isExpanded === true}
						onCellExpand={() => {
							toggleSubRow(row.id, row.values);
						}}
					/>
				)}
				<Stack direction="row" alignItems="center" flexGrow={1}  sx={{width: '100%'}}>
					<RowTypography
						isSuccessor={!row.values}
						{...(isCellUpdated && { color: `#FD9532 !important` })}
						isEditing={isEditing}
						sx={{
							width: (!hasChildren && idx === 0) ? 'calc(100% - 20px)' : 'auto',
							flexGrow: 1,
							overflow: 'hidden',
							fontWeight: 500,
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: 'calc(100% - 40px)',
							marginLeft: "8px",
							marginRight: "4px",
							...(!isCellUpdated && unknownCellColor && { color: unknownCellColor })
						}}
					>
						{getCellDisplayValue(row[column.key])}
					</RowTypography>
					{((hasChildren && idx === 0 && isGroupBy && row.isResource === false && groupBy.toLowerCase() !== 'resourcegroup')
					|| (idx === 1 && column.key === 'Resource' && row.isResource && hasChildren && groupBy.toLowerCase() === 'resourcegroup')) && (
						<HistoryIcon  cursor="pointer" sx={{color: `${colors.table.text} !important`}} fontSize="medium" onClick={() => fetchUpdateHistory(row)} />
					)}
				</Stack>
			</Box>
		);
	};

	const renderEditableColumns = (column: any, row: any, onRowChange: any, isCellSelected: boolean) => {
		const isCellEdited =
			(isTreeView ? nonEditedUnGroupList.current : nonEditedList.current)[row.id]?.[column.key] !==
			row[column.key];

		return (
			<SearchFilterDropdown
				mappingType="finopsResourceMapping"
				columnName={column.key}
				isCellSelected={isCellSelected}
				value={row[column.key]}
				onChange={(newValue: string) =>
				onRowChange({ ...row, [column.key]: newValue }, false)
				}
				options={[]}
				fieldValueOptionsCallback={(input: string) =>
				getInputOptions(input, column.key)
				}
				{...(isCellEdited && {
				cellInputStyle: { color: '#FD9532' }
				})}
			/>
		);
	};

	const renderSpecialColumns = (column: any, row: any, hasChildren: boolean, idx: number, onRowChange: any, isCellSelected: boolean) => {
		if (column.key === 'PepwiseSector')
			return renderRowTypography(row.SectorCount, row.PepwiseSector, row.BusinessSector, idx, 'SECTORS');
		if (column.key === 'PepwiseProject')
			return renderRowTypography(row.ProjectCount, row.PepwiseProject, row.ProjectName, idx, 'PROJECTS');
		if (column.key === 'PepwiseProgram')
			return renderRowTypography(row.ProgramCount, row.PepwiseProgram, row.Program, idx, 'PROGRAMS');
		if (column.key === 'AllocPercent') {
			return (
				<RowTypography
					sx={{
						fontWeight: 500,
						...(getUnknownCellColor(row.AllocPercent) && { color: getUnknownCellColor(row.AllocPercent) })
					}}
					key={`alloc-${row.AllocPercent ?? ''}-${idx}`}
				>
					{isEmptyCellValue(row.AllocPercent) ? 'Unknown' : formatAllocPercent(row.AllocPercent)}
				</RowTypography>
			);
		}
		if (column.key === 'PepwiseEnvironment') {
			let color = `${colors.table.text} !important`;

			if (!row.PepwiseEnvironment) {
				color = '#71758F !important';
			} else if (row.PepwiseEnvironment !== row.Environment) {
				color = '#FD9532';
			}

			return (
				<RowTypography
					isSuccessor={!row.values}
					isEditing={isEditing}
					sx={{
						width: !hasChildren && idx === 0 ? 'calc(100% - 20px)' : 'auto',
						color: color
					}}
				>
					{row.PepwiseEnvironment ? row.PepwiseEnvironment.split(',')[0] : 'Unknown'}
				</RowTypography>
			);
		}

		if (!hasChildren && isEditing && column.isEditable) {
			return renderEditableColumns(column, row, onRowChange, isCellSelected);
		}

		return null;
	};

	const ResourceCellFormatter: React.FC<ResourceCellFormatterProps> = (props) => {
		const {
			row,
			column,
			isCellSelected,
			idx,
			groupBy,
			editedRows,
			onRowChange
		} = props;
		const hasChildren = (groupBy === 'ResourceGroup' && !!row.values) || (groupBy !== 'ResourceGroup' && (!!row.group_count && row.group_count >= 1));

		const specialColumn = renderSpecialColumns(column, row, hasChildren, idx, onRowChange, isCellSelected);
		if (specialColumn) {
			return specialColumn;
		}

		// ---------- Default render ----------
		return renderDefaultResourceCell({
			row,
			column,
			idx,
			hasChildren,
			isCellSelected,
			editedRows,
			groupBy,
		});
	};

	const resourceCellFormatter = (idx: number, groupBy: any, editedRows: Record<string, any>) => {
		const formatter = ({ row, isCellSelected, onRowChange, column }: any) => (
			<ResourceCellFormatter
				row={row}
				column={column}
				isCellSelected={isCellSelected}
				idx = {idx}
				groupBy = {groupBy}
				editedRows = {editedRows}
				onRowChange = {onRowChange}
			/>
		);
		return formatter;
	};

	function getColumns(editedRows: Record<string, any>): Array<any> {
		// For checkBox
		const checkboxColumn = {
			key: 'selected',
			name: 'HeaderCheckboxMapping',
			frozen: true,
			formatter: checkboxFormatter(),
			minWidth: 40
		};

		const [groupBy, viewType] = queryPreference.split('_');
		const columns = [];
		for (const value of columnNames) {
			const field = value.name;
			if (field.toLowerCase() !== groupBy.toLowerCase()) {
				columns.push(value);
			} else {
				columns.unshift(value);
			}
		}
		const columnArray: any = [
			// Add the "Actions" column first
			{
				key: 'actions',
				name: 'Actions',
				editable: false,
				frozen: true,
				formatter: actionFormatter(),
				width: 20,
			},
			// Add the "Checkbox" column second
			checkboxColumn,
			// Add all other columns
			...columns.map((col: any, idx: number) => ({
				key: col.name,
				name: col.label,
				editable: () => col.isEditable,
				resizable: true,
				frozen: ((col.name === groupBy || ['Resource'].includes(col.name)) && (viewType != 'flatall')),
				formatter: resourceCellFormatter(idx, groupBy, editedRows),
				...(tableDataList && tableDataList.length > 0
					? { minWidth: col.name === 'actions' ? 20 : 200, maxWidth: col.name === 'actions' ? 20 : undefined }
					: { minWidth: col.name === 'actions' ? 20 : 180, maxWidth: col.name === 'actions' ? 20 : undefined })
			}))
		];

		return columnArray;
	}

	const renderRowTypography = (count: any, value: any, DBValue: any, idx: any, label: any) => {
		const hasValue = Boolean(count > 1 || value);

		let labelValue = 'Unknown';

		if (count > 1) {
			labelValue = `${count} ${label}`;
		} else if (value) {
			labelValue = value?.split(',')[0];
		}

		let textColor = `${colors.table.text} !important`;

		if (!hasValue) {
			textColor = '#71758F !important';
		} else if (value !== DBValue) {
			textColor = '#FD9532 !important';
		}

		return (
			<RowTypography
				sx={{
					color: "#003771 !important",
					fontWeight: 500,
					padding: '4px 10px',
					marginRight: '5px',
					borderRadius: '20px',
					backgroundColor: count > 1 ? '#299BFF26 !important' : 'transparent !important',
					display: 'inline-flex',
					alignItems: 'center',
					width: 'fit-content',
					lineHeight: '14px',
				}}
				key={idx}
				title={labelValue}
			>
				{labelValue}
			</RowTypography>
		);
	};

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [bulkSelectedRows, setBulkSelectedRows] = useState<Record<string, Row>>({});

	const [tableDataList, setTableDataList] = useState<any[]>([]);
	const nonEditedList = useRef<Record<string, Row>>({});
	const nonEditedUnGroupList = useRef<Record<string, Row>>({});
	const [loading, setLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);

	const [filterData, setFilterData] = useState<Row[]>([]);
	const [filterInfo, setFilterInfo] = useState({} as any);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isProjectAllocationModalOpen, setIsProjectAllocationModalOpen] = useState<boolean>(false);
	const [isUpdateHistoryOpen, setIsUpdateHistoryOpen] = useState<boolean>(false);
	const [isResourceConflictModalOpen, setIsResourceConflictModalOpen] = useState<boolean>(false);
	const [modalTitle, setModalTitle] = useState<string>('Allocation Conflicts in Demo Project');
	const [modalWidth, setModalWidth] = useState<ModalWidth>('sm');
	const [currentRow, setCurrentRow] = useState({} as any);
	const [curRow, setCurRow] = useState({} as any);
	const [isConflicts, setIsConflicts] = useState<boolean>(true);

	const [editedRows, setEditedRows] = useState<any>(initialEditRowState);
	const nonEditedRows = useRef<any>(initialEditRowState);

	//resolve conflict modal title change
	const handleTitleChange = (newTitle: string) => {
		setModalTitle(newTitle);
	};

	//modal width change
	const handleWidthChange = (newWidth: ModalWidth) => {
		setModalWidth(newWidth);
	};

	// fetch resource update history
	const fetchUpdateHistory = (row: any) => {
		setCurRow({ ...row });
		setIsUpdateHistoryOpen(true);
	};

	async function listTableData({ pageNumber, size, sortingInfo, resourceGroupFilterInfo, queryProps }: any) {
		if (queryProps.query === null || !queryProps.query) {
				clearTableData();
				return;
		}
		const signal = abortControllerRef.current?.signal;
		try {
			setLoading(true);
			Object.keys(resourceGroupFilterInfo).forEach((key) => {
				resourceGroupFilterInfo[key] = resourceGroupFilterInfo[key].filter((value: any) => value !== null);
			});

			const [groupBy, viewType] = queryPreference.split('_');
			let res;
			res = await getFinopsResourceMappingList({
				page: pageNumber,
				size,
				totalPages: totalPages,
				sortingInfo: sortingInfo,
				...(queryProps.isSearch
					? {
							searchField: 'quicksearch',
							searchText: queryProps.query
						}
					: {
							searchField: 'detail',
							...queryProps.query
						}),
				...((viewType === 'tree' || viewType === 'inttree' || viewType === 'jobtree') && {
					groupBy
				}),
				viewType,
				signal,
				includeCost: true
			});

			if (res.success) {
				//Adding external id
				const listData = res.data.result.map((row: any) => ({
					// id: crypto.randomUUID(),
					...row,
					values: row.values
						? row.values.map((secondRow: any) => ({
								id: crypto.randomUUID(),
								...secondRow,
								values: secondRow.values
									? secondRow.values.map((thirdRow: any) => ({
											id: crypto.randomUUID(),
											...thirdRow
									  }))
									: undefined
						  }))
						: undefined
				}));
				setTableDataList(listData);
				setPaginationData(res.data.page);
				setPage(res.data.page.currentPage);
				setPageSize(res.data.page.size);
				setTotalPages(res.data.page.totalPages);
				setSortingInfo(sortingInfo);
				setFilterData(res.data.filterOptions);
				setFilterInfo(resourceGroupFilterInfo);
				setLoading(false);
				setBulkSelectedRows({});
				if (typeof onResourceTypeCostChange === 'function') {
					onResourceTypeCostChange(res.data.resourceTypeCost ?? []);
				}
				const resourceGroupsState = {
					page: res.data.page,
					filterOptions: res.data.filterOptions,
					pageSize: size,
					pageNumber,
					sortingInfo,
					filterInfo: resourceGroupFilterInfo
				};
				localStorage.setItem('resourceGroupsState ', JSON.stringify(resourceGroupsState));
				localStorage.removeItem('finopsGetResource'); 
			}
		} catch (error) {
			console.error(error);
			if (typeof error === "object" && error !== null && "code" in error && error?.code === 'ERR_CANCELED') {
				console.warn('Request was canceled by user');
				eventEmitter?.emit("showSnackbar", "Request was canceled", "info");
				return;
			}
			clearTableData();
			if (typeof error === "object" && error !== null && "response" in error) {
				const err = error as { response: { status: number } };
				if (err.response.status === 503)
					eventEmitter.emit("showSnackbar", "Service temporarily unavailable. Please try again later.", "info");
				else if (err.response.status === 504) {
					clearTableData();
					eventEmitter.emit("showSnackbar", "Gateway Timeout", "info");
				}
			}
		} finally {
			setLoading(false);
			setIsAbort(false);
		}
	}

	// export
	const columnMap: { [key: string]: string } = {
		"Resource": "Resource",
		"Resource Group": "Resource Group",
		'Resource Type': 'Resource Type',
		"Allocation %": "Allocation %",
		"Pepwise Sector": "Pepwise Sector",
		"Pepwise Environment": "Pepwise Environment",
		"Pepwise Project": "Pepwise Project",
		"Pepwise Program": "Pepwise Program",
		"Business Sector": "Business Sector",
		"Project Name": "Project Name",
		"Program": "Program",
		"Environment": "Environment",
		"Subscription": "Subscription",
		"Consumed Service": "Consumed Service",
		// 'Meter Category': 'Meter Category',
		"Location": "Location",
		"Org Grp Code Project Code": "Org Grp Code Project Code",
		"Project No": "Project No",
		"Billing Information": "Billing Information",
		"Cluster Name": "Cluster Name",
		"Cluster Job Source": "Cluster Job Source",
		"Databricks Instance Pool Id": "Databricks Instance Pool Id",
		"DF Sectorname": "DF Sectorname",
		"DF Projectcode": "DF Projectcode",
		"DF Data Assetname": "DF Data Assetname",
		"Sql Endpoint Id": "Sql Endpoint Id",
		"Kubernetes pvc Namespace": "Kubernetes pvc Namespace"
	  };
	  
			async function exportTableData({ pageNumber, size, sortingInfo, resourceGroupFilterInfo, queryProps, prefColumns }: any) {
				let formatType = "EXCEL"
				let tableName = "Finops Resource Mapping List"
			const exportColumns = prefColumns.map((prefCol: string) => columnMap[prefCol])
				.filter((col: string) => col !== null && col !== undefined);
				
			const [groupBy, viewType] = queryPreference.split('_');
			await exportGetFinopsResourceMappingList({
				exportColumns,
				formatType,
				page: pageNumber,
				size,
				totalPages: totalPages,
				sortingInfo: sortingInfo,
				...(queryProps.isSearch
					? {
							searchField: 'quicksearch',
							searchText: queryProps.query
						}
					: {
							searchField: 'detail',
							...queryProps.query
						}),
				...((viewType === 'tree' || viewType === 'inttree' || viewType === 'jobtree') && {
					groupBy
				}),
				viewType,
				tableName
			});
			};

	const clearTableData = () => {
		setTableDataList([]);
		setPaginationData({ totalElements: 0, totalPages: 0 });
		setPage(1);
		setPageSize(100);
		setTotalPages(0);
	};

	const handleResourceColumnClick = (row: any) => {
		setBulkSelectedRows({});
		setCurrentRow({ ...row });
		handleTitleChange('Create/Modify Allocation');
		handleWidthChange('lg');
		setIsProjectAllocationModalOpen(true);
	};

	const handleEditCell = (rows: any, editData: any) => {
		const editedColumnkey = editData.column.key;
		const editedRow = rows[editData.indexes[0]];
		setEditedRows((prevEditedRows: any) => ({
			...prevEditedRows,
			rowsToUpdate: {
				...prevEditedRows.rowsToUpdate,
				[editedRow.id]: editedRow
			},
			[editedRow.id]: {
				...prevEditedRows[editedRow.id],
				[editedColumnkey]: true
			}
		}));
		if (isTreeView) {
			let parentRow: any = {};
			for (let i = editData.indexes[0]; i >= 0; i--) {
				if (rows[i]?.name) {
					parentRow = rows[i];
					break;
				}
			}
			const index = parentRow.values.findIndex((row: any) => row.id === editedRow.id);
			parentRow.values[index][editedColumnkey] = editedRow[editedColumnkey];
			if (parentRow.children?.[index]?.[editedColumnkey]) {
				parentRow.children[index][editedColumnkey] = editedRow[editedColumnkey];
			}
		}
		setTableDataList(rows);
	};

	const setNonEditedTableList = () => {
		if (isTreeView) {
			const defaultList: Record<string, Row> = {};
			const unGroupedList: Record<string, Row> = {};
			tableDataList.forEach((row) => {
				defaultList[row.id] = row;
				if (row.values) {
					row.values.forEach((value: Row) => {
						unGroupedList[value.id] = value;
					});
				}
			});
			nonEditedList.current = structuredClone(defaultList);
			nonEditedUnGroupList.current = structuredClone(unGroupedList);
		} else {
			nonEditedList.current = structuredClone(
				tableDataList.reduce((acc, row) => {
					acc[row.id] = row;
					return acc;
				}, {})
			);
		}
	};

	// save edited rows
	const handleSave = async () => {
		console.log('save', editedRows);
		// deepCopy with structuredClone
		nonEditedRows.current = structuredClone(editedRows);
		setNonEditedTableList();
		setIsEditing(false);
	};

	const handleCancel = async () => {
		setIsEditing(false);
		setTableDataList(structuredClone(Object.values(nonEditedList.current)));
		setEditedRows(structuredClone(nonEditedRows.current));
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		listTableData({
			pageNumber: value,
			size: pageSize,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	};

	const handlepageSizeChange = (event: any) => {
		listTableData({
			pageNumber: 1,
			size: event.target.value,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	};
	const handleSorting = (sortingInfo: any) => {
		listTableData({
			pageNumber: page,
			size: pageSize,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	};
	const handleFilter = (filterInfo: any) => {
		setFilterInfo(filterInfo);
		listTableData({
			pageNumber: 1,
			size: pageSize,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	};

	//Handle for select all rows
	const handleHeaderSelectAll = (event: any) => {
		const isChecked = event.target.checked;
		setHeaderCheckbox(isChecked);

		const updatedBulkSelectedRows = isChecked
			? tableDataList.reduce((acc: Record<string, any>, row: any) => {
					acc[row.id] = row;
					if (row.values && Array.isArray(row.values)) {
						// For tree view, add each nested row as seperate row
						row.values.forEach((nestedRow: any) => {
							acc[nestedRow.id] = nestedRow;
							if (nestedRow.values && Array.isArray(nestedRow.values)) {
								// For tree view, add each nested nestedRow as seperate nestedRow
								nestedRow.values.forEach((childNested: any) => {
									acc[childNested.id] = childNested;
								});
							}
						});
					}
					return acc;
			  }, {})
			: {};

		setBulkSelectedRows(updatedBulkSelectedRows);
	};

	const handleModalSave = (formData: any, changedCells: any, rowsToUpdate: any) => {
		setEditedRows((prev: any) => {
			const changedCellsClone = structuredClone(changedCells);
			const value = Object.keys(changedCellsClone).reduce(
				(acc: any, key) => {
					acc[key] = {
						...acc[key],
						...changedCellsClone[key]
					};
					return acc;
				},
				{ ...prev }
			);

			nonEditedRows.current = structuredClone(value);
			return value;
		});
		setTableDataList((prev: any) => {
			const clonedValue = structuredClone(prev);
			if (isTreeView) {
				const defaultList: Record<string, Row> = {};
				const unGroupedList: Record<string, Row> = {};
				prev.forEach((row: any, idx: number) => {
					if (rowsToUpdate[row.id]) {
						defaultList[row.id] = rowsToUpdate[row.id];
						clonedValue.splice(idx, 1, rowsToUpdate[row.id]);
					} else {
						defaultList[row.id] = row;
					}
					if (row.values) {
						row.values.forEach((value: Row, valIdx: number) => {
							if (rowsToUpdate[value.id]) {
								unGroupedList[value.id] = rowsToUpdate[value.id];
								clonedValue[idx].values.splice(valIdx, 1, rowsToUpdate[value.id]);
							} else {
								unGroupedList[value.id] = value;
							}
						});
					}
				});
				nonEditedList.current = structuredClone(defaultList);
				nonEditedUnGroupList.current = structuredClone(unGroupedList);
			} else {
				nonEditedList.current = structuredClone(
					prev.reduce((acc: any, row: any, idx: number) => {
						if (rowsToUpdate[row.id]) {
							acc[row.id] = rowsToUpdate[row.id];
							clonedValue.splice(idx, 1, rowsToUpdate[row.id]);
						} else {
							acc[row.id] = row;
						}
						return acc;
					}, {})
				);
			}
			return clonedValue;
		});
	};

	useEffect(() => {}, [bulkSelectedRows]);

	useEffect(() => {
		clearTableData();
		setIsQueryPrefChange(false);
	}, [isQueryPrefChange]);

	useEffect(() => {
		setTableTitle(tableTitles[queryPreference] ?? 'Resources');
		listTableData({
			page,
			pageSize,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	}, [queryProps]);

	useEffect(() => {
		if(isReapplied)
			clearTableData();
	}, [isReapplied]);

	const handleModalClose = () => {
		setBulkSelectedRows({});
		setIsModalOpen(false);
		setHeaderCheckbox(false);
	};

	const handleResourceConflictsModalClose = () => {
		setIsConflicts(true);
		setBulkSelectedRows({});
		setIsResourceConflictModalOpen(false);
		if (headerCheckboxRef.current) {
			headerCheckboxRef.current.checked = false;
		  }
	};

	const handleProjectAllocationModalClose = () => {
		setIsProjectAllocationModalOpen(false);
		setCurrentRow({});
	};

	const refetchTableList = () => {
		listTableData({
			page,
			pageSize,
			sortingInfo,
			resourceGroupFilterInfo: parseFilter(filterInfo),
			queryProps
		});
	}

	return loading ? (
		<LoaderComponent pt={10} />
	) : (
		<>
			<Stack justifyContent="flex-end">
				{isEditing ? (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: 'auto', gap: 2 }}>
						<Button variant="pepwiseSecondaryOutline2" sx={{ width: '8rem' }} onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="pepwiseSecondary" sx={{ width: '8rem' }} onClick={handleSave}>
							Save
						</Button>
					</Box>
				) : tableDataList.length > 0 && (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: 'auto', gap: 2 }}>
						<Button
							disabled={Object.keys(bulkSelectedRows).length === 0}
							variant="pepwiseSecondary"
							sx={{ width: '8rem', alignSelf: 'flex-end' }}
							onClick={() => {
								setIsModalOpen(true);
								handleTitleChange('Resources Bulk Update');
								handleWidthChange('md');
							}}
						>
							EDIT
						</Button>
					</Box>
				)}
			</Stack>
			<CustomizedDataGrid
				title={<HighlightedText text={tableTitle} />}
				tableType="resource-list"
				tableColumns={getColumns(nonEditedRows.current)}
				tableData={tableDataList}
				defaultColumnNames={defaultColumnNames}
				onCellEdit={handleEditCell}
				onSorting={handleSorting}
				currSorting={sortingInfo}
				filterDataOptions={filterData}
				onHeaderSelectAll={handleHeaderSelectAll}
				onFilterCheck={handleFilter}
				currFilter={filterInfo}
				page={page}
				pageSize={pageSize}
				paginationData={paginationData}
				listLength={tableDataList?.length}
				handlepageSizeChange={handlepageSizeChange}
				handlePageChange={handlePageChange}
				showGoToPage={true}
				isDateRangeFilter={false}
				isDisabled={false}
				isFetching={loading}
				isEditing={isEditing}
				childRowClass={true}
				isShowMoreButton = {true}
				showExport = {true}
				exportTableData={(prefColumns: any) =>
					exportTableData({ page, pageSize, sortingInfo, resourceGroupFilterInfo: parseFilter(filterInfo), queryProps, prefColumns })
				}
				headerCheckbox={headerCheckbox}
				isSortIcon = {true}
				isLighterTheme = {true}
			/>
			{/* Bulk edit modal */}
			<DialogueModal
				isOpen={isModalOpen}
				title={modalTitle}
				handleClose={() => {
					if (modalTitle === 'Allocation Modified') {
						refetchTableList();
					}
					handleModalClose();
				}}
				width={modalWidth}
			>
				<BulkEditModalConfirm
					onClose={handleModalClose}
					onSave={handleModalSave}
					onCancel={() => setIsModalOpen(false)}
					bulkSelectedRows={bulkSelectedRows}
					setBulkSelectedRows={setBulkSelectedRows}
					queryPreference={queryPreference}
					onTitleChange={handleTitleChange}
					onWidthChange={handleWidthChange}
					onConfirm={() => {
						refetchTableList();
					}}
					allocationType={{
						key: queryPreference?.split?.('_')?.[0],
						label: tableTitle
					}}
				/>
			</DialogueModal>

			{/* Resource Conflict Modal */}
			<DialogueModal
				isOpen={isResourceConflictModalOpen}
				title={modalTitle}
				handleClose={handleResourceConflictsModalClose}
				width={modalWidth}
				dialogContentProps={{
					sx: { p: 0 }
				}}
			>
				{isConflicts ? (
					<AllocationConflictModal onTitleChange={handleTitleChange} setIsConflicts={setIsConflicts} />
				) : (
					<ResolveAllocationConflictsModal
						bulkSelectedRows={bulkSelectedRows}
						onTitleChange={handleTitleChange}
						onWidthChange={handleWidthChange}
						modalTitle={modalTitle}
					/>
				)}
			</DialogueModal>

			{/* Resource Allocation Modal */}
			<DialogueModal
				isOpen={isProjectAllocationModalOpen}
				title={modalTitle}
				handleClose={() => {
					if (modalTitle === 'Allocation Modified') {
						refetchTableList();
					}
					handleProjectAllocationModalClose();
				}}
				width={modalWidth}
				dialogContentProps={{
					sx: { p: 0 }
				}}
			>
				<ResourceAllocationModal
					allocationType={{
						key: queryPreference?.split?.('_')?.[0],
						label: tableTitle
					}}
					currentRow={currentRow}
					onCancel={() => setIsProjectAllocationModalOpen(false)}
					onTitleChange={handleTitleChange}
					onWidthChange={handleWidthChange}
					modalTitle={modalTitle}
					//Re-fetch the updated table data inside tableSection once project allocation completed
					onConfirm={() => {
						refetchTableList();
					}}
				/>
			</DialogueModal>

			{/* Resource update history */}
			<Drawer anchor="bottom" open={isUpdateHistoryOpen} onClose={() => setIsUpdateHistoryOpen(false)}>
				<UpdateHistory curRow={curRow} setIsUpdateHistoryOpen={setIsUpdateHistoryOpen} />
			</Drawer>
		</>
	);
};

export default TableSection;
